/**
 * Session management hook and context
 * Handles session creation, refresh, and state
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { createSession, refreshSession } from "../api/client";

interface SessionState {
  sessionId: string | null;
  sessionKey: string | null;
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
}

interface SessionContextValue extends SessionState {
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

const SESSION_REFRESH_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours
const SESSION_STORAGE_KEY = "totemfeed_session_id";

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({
    sessionId: null,
    sessionKey: null,
    isReady: false,
    isLoading: true,
    error: null,
  });

  // Initialize session
  const initSession = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Check if we have a stored session ID (cookie should handle actual auth)
      const storedSessionId = localStorage.getItem(SESSION_STORAGE_KEY);

      if (storedSessionId) {
        // Try to refresh existing session
        try {
          await refreshSession();
          setState((prev) => ({
            ...prev,
            sessionId: storedSessionId,
            isReady: true,
            isLoading: false,
          }));
          return;
        } catch {
          // Session expired, create new one
          localStorage.removeItem(SESSION_STORAGE_KEY);
        }
      }

      // Create new session
      const response = await createSession();
      localStorage.setItem(SESSION_STORAGE_KEY, response.session_id);

      setState({
        sessionId: response.session_id,
        sessionKey: response.session_key,
        isReady: true,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isReady: false,
        isLoading: false,
        error: err instanceof Error ? err.message : "Failed to initialize session",
      }));
    }
  }, []);

  // Refresh session
  const refresh = useCallback(async () => {
    try {
      await refreshSession();
    } catch (err) {
      console.error("Failed to refresh session:", err);
      // Re-initialize if refresh fails
      await initSession();
    }
  }, [initSession]);

  // Initialize on mount
  useEffect(() => {
    initSession();
  }, [initSession]);

  // Set up periodic refresh
  useEffect(() => {
    if (!state.isReady) return;

    const interval = setInterval(refresh, SESSION_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [state.isReady, refresh]);

  // Refresh on window focus (for long-idle tabs)
  useEffect(() => {
    const handleFocus = () => {
      if (state.isReady) {
        refresh();
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [state.isReady, refresh]);

  const value: SessionContextValue = {
    ...state,
    refresh,
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
}

